import { createEffect, createMemo } from "solid-js"
import { createSimpleContext } from "@mux/ui/context"
import { showToast } from "@mux/ui/toast"
import type { ControlProvider } from "@/context/provider-types"
import { useProviders } from "@/hooks/use-providers"
import { createAccountResource } from "@/context/account-preference"
import { readModelPreferencesSnapshot, seedModelPreferencesSnapshot } from "@/context/model-preference-cache"
import { changeModelPreferences, getModelPreferences, type ModelPreferences, type ModelPreferenceChanges } from "@/transport/preferences"
import type { PromptResponseStyle } from "@/transport/prompt"
import { modelLabel } from "@/utils/model-label"
import { providerInstanceLabel, providerInstanceName } from "@/utils/provider-instance"
import { modelVisibilityKey, resolveModelVisibility, selectVisibleModels } from "@/utils/model-visibility"
import { addRecentModel, type ModelKey } from "@/utils/model-recent"
import { describeError } from "@/utils/error-message"

export type { ModelKey } from "@/utils/model-recent"

export function availableProviderModels(providers: ControlProvider[]) {
  return providers
    .filter((provider) => provider.options?.runtimeConfigured !== false)
    .flatMap((provider) => Object.values(provider.models).map((model) => ({ ...model, provider })))
}

function projectModelPreferences(current: ModelPreferences, change: ModelPreferenceChanges): ModelPreferences {
  let user = current.user
  for (const entry of change.model_visibility ?? []) {
    const key = modelVisibilityKey(entry)
    const existing = user.filter((row) => modelVisibilityKey(row) === key)
    // Pending explicit choices must override legacy, untimestamped curation records.
    const updatedAt = Math.max(1, ...existing.map((row) => row.updatedAt ?? 0))
    user = [
      ...user.filter((row) => modelVisibilityKey(row) !== key),
      { ...existing.at(-1), ...entry, updatedAt },
    ]
  }
  return {
    user,
    recent: change.model_recent
      ? addRecentModel(current.recent, change.model_recent, change.model_recent.usedAt)
      : current.recent,
    contextWindows: change.model_context_windows
      ? replaceModelRow(current.contextWindows, change.model_context_windows, change.model_context_windows.contextWindow ? change.model_context_windows : undefined)
      : current.contextWindows,
    variants: change.model_variants
      ? replaceModelRow(current.variants, change.model_variants, change.model_variants.variant === null ? undefined : { ...change.model_variants, variant: change.model_variants.variant })
      : current.variants,
    serviceTiers: change.model_service_tiers
      ? replaceModelRow(current.serviceTiers, change.model_service_tiers, change.model_service_tiers.serviceTier ? change.model_service_tiers : undefined)
      : current.serviceTiers,
    responseStyles: change.model_response_styles
      ? replaceModelRow(current.responseStyles, change.model_response_styles, change.model_response_styles.responseStyle ? { ...change.model_response_styles, responseStyle: change.model_response_styles.responseStyle } : undefined)
      : current.responseStyles,
    providerTitles: change.provider_titles ? { ...current.providerTitles, ...change.provider_titles } : current.providerTitles,
  }
}

/** Replaces a model's row in a per-model preference, or removes it when `row` is undefined. */
function replaceModelRow<T extends ModelKey>(rows: T[], model: ModelKey, row: T | undefined) {
  const key = modelVisibilityKey(model)
  const rest = rows.filter((entry) => modelVisibilityKey(entry) !== key)
  return row ? [...rest, row] : rest
}

function createModelPreferences() {
  const cached = readModelPreferencesSnapshot()
  const resource = createAccountResource<ModelPreferences, ModelPreferenceChanges>(
    cached ?? { user: [], recent: [], contextWindows: [], variants: [], serviceTiers: [], responseStyles: [], providerTitles: {} },
    getModelPreferences, changeModelPreferences, projectModelPreferences,
  )
  createEffect(() => {
    const value = resource.value()
    if (resource.ready() && !resource.saving()) seedModelPreferencesSnapshot(value)
  })
  return {
    ...resource,
    /** Preferences to choose models by: loaded, or this device's copy of the account's last. */
    known: () => !!cached || resource.ready(),
  }
}

/** The models `providers` offer, read through the account's model preferences. */
function createModels(providers: ReturnType<typeof useProviders>, preferences: ReturnType<typeof createModelPreferences>) {
  /** Saves a change; resolves whether the account kept it. Failures are shown here. */
  const save = (change: ModelPreferenceChanges) => preferences.save(change).then(() => true, (error) => {
    showToast({
      title: "Could not save model preferences",
      description: describeError(error),
    })
    return false
  })
  const available = createMemo(() => availableProviderModels(providers.connected()))
  const visibility = createMemo(() => {
    const map = new Map<string, ModelPreferences["user"][number]>()
    for (const item of preferences.value().user) {
      const key = modelVisibilityKey(item)
      const current = map.get(key)
      if (!current || (item.updatedAt ?? 0) >= (current.updatedAt ?? 0)) map.set(key, item)
    }
    return map
  })
  const catalog = createMemo(() => available().map((m) => ({
    ...m, name: modelLabel(m.name.replace("(latest)", "").trim()), latest: m.latest === true,
  })))
  const list = createMemo(() => catalog().filter((model) => !model.unlisted))
  const find = (key: ModelKey) => catalog().find((m) => m.id === key.modelID && m.provider.id === key.providerID)
  const visible = (model: ModelKey) => resolveModelVisibility(
    { ...model, defaultVisible: find(model)?.defaultVisible ?? true }, visibility().get(modelVisibilityKey(model)),
  )
  return {
    preferences,
    ready: preferences.ready,
    known: preferences.known,
    get loading() { return providers.loading },
    list,
    selectable: createMemo(() => selectVisibleModels(list(), visible)),
    find,
    visible,
    setVisibility(model: ModelKey, state: boolean) {
      void save({ model_visibility: [{ ...model, visibility: state ? "show" : "hide" }] })
    },
    setProviderVisibility(providerID: string, modelIDs: string[], state: boolean) {
      if (!modelIDs.length) return
      void save({ model_visibility: modelIDs.map((modelID) => ({ providerID, modelID, visibility: state ? "show" : "hide" })) })
    },
    recent: {
      list: createMemo(() => preferences.value().recent),
      push(model: ModelKey & { variant?: string | null }) {
        void save({ model_recent: { ...model, variant: model.variant || undefined, usedAt: Date.now() } })
      },
    },
    /** The account's context window default for a model; new and Default chats use it. */
    contextWindow(model: ModelKey) {
      const key = modelVisibilityKey(model)
      return preferences.value().contextWindows.find((row) => modelVisibilityKey(row) === key)?.contextWindow
    },
    /** Undefined removes the model's default. */
    setContextWindow(model: ModelKey, contextWindow: number | undefined) {
      void save({ model_context_windows: { providerID: model.providerID, modelID: model.modelID, contextWindow: contextWindow ?? 0 } })
    },
    /**
     * The thinking level new chats with a model start at: a level, "" for
     * Auto, or undefined when the account has none and the model's default
     * applies.
     */
    variant(model: ModelKey) {
      const key = modelVisibilityKey(model)
      return preferences.value().variants.find((row) => modelVisibilityKey(row) === key)?.variant
    },
    /** Every model's starting thinking level the account has set. */
    variantDefaults: createMemo(() => preferences.value().variants),
    /** Undefined removes the model's default. */
    setVariant(model: ModelKey, variant: string | undefined) {
      void save({ model_variants: { providerID: model.providerID, modelID: model.modelID, variant: variant ?? null } })
    },
    /** The speed tier every chat with a model is sent at; undefined is standard speed. */
    serviceTier(model: ModelKey) {
      const key = modelVisibilityKey(model)
      return preferences.value().serviceTiers.find((row) => modelVisibilityKey(row) === key)?.serviceTier
    },
    setServiceTier(model: ModelKey, serviceTier: string | undefined) {
      return save({ model_service_tiers: { providerID: model.providerID, modelID: model.modelID, serviceTier: serviceTier ?? "" } })
    },
    /** The response style every turn with a model uses; undefined is the account's. */
    responseStyle(model: ModelKey) {
      const key = modelVisibilityKey(model)
      return preferences.value().responseStyles.find((row) => modelVisibilityKey(row) === key)?.responseStyle
    },
    setResponseStyle(model: ModelKey, responseStyle: PromptResponseStyle | undefined) {
      return save({ model_response_styles: { providerID: model.providerID, modelID: model.modelID, responseStyle: responseStyle ?? "" } })
    },
    /** What every surface calls a provider: the account's name for it, else its default label. */
    providerLabel(provider: Pick<ControlProvider, "id" | "name" | "options">) {
      return providerInstanceLabel(provider, preferences.value().providerTitles[provider.id])
    },
    /** The provider's name and signed-in account apart, for surfaces that show the account under the name. */
    providerName(provider: Pick<ControlProvider, "id" | "name" | "options">) {
      return providerInstanceName(provider, preferences.value().providerTitles[provider.id])
    },
    /** What chat surfaces call a model: `Provider / Model`, the provider by its name without the signed-in account. */
    modelName(key: ModelKey) {
      const model = find(key)
      const titles = preferences.value().providerTitles
      // A model whose provider is no longer connected keeps its provider's name; only its ID is known.
      if (!model) return `${providerInstanceName({ id: key.providerID, name: key.providerID }, titles[key.providerID]).name} / ${key.modelID}`
      return `${providerInstanceName(model.provider, titles[model.provider.id]).name} / ${model.name}`
    },
    /** An empty title restores the default label. Resolves whether the account kept it. */
    setProviderTitle(providerID: string, title: string) {
      return save({ provider_titles: { [providerID]: title.trim() } })
    },
  }
}

const models = createSimpleContext({
  name: "Models",
  gate: false,
  init: () => createModels(useProviders(), createModelPreferences()),
})

export const useModels = models.use
export const ModelsProvider = models.provider
